import { AppSettings } from "@/AppSettings";
import { ConfigurationSourceDataItem, ConfigurationSourceDataMap, Metadata, SourceDataFieldType, SourceDataItemGroupType, SourceDataPriority, Trial } from "@/api/dtos";
import { useMemo } from "react";
import { ConfigurationType } from ".";

export const fieldTypesWithValues = [
    SourceDataFieldType.Radio, 
    SourceDataFieldType.MultiSelect, 
    SourceDataFieldType.Select
];

const excludedConfigurationSourceDataItemCopyProperties: Partial<Record<keyof ConfigurationSourceDataItem, boolean>> = {
    id: true,
    trialId: true,
    configurationSourceDataId: true,
    studyEventOid: true,
    caseReportFormOid: true,
    caseReportFormVersionOid: true,
    itemGroupName: true,
    itemName: true,
    linked: true,
    enteredBy: true,
    enteredDate: true,
    modifiedBy: true,
    modifiedDate: true
}

export const copyConfigurationSourceDataItemSettings = (
    source: ConfigurationSourceDataItem,
    target: ConfigurationSourceDataItem,

) => {
    for (let pName in source) {
        const propertyName = pName as keyof ConfigurationSourceDataItem;

        if (!excludedConfigurationSourceDataItemCopyProperties[propertyName]) {
            (target[propertyName] as any) = source[propertyName] as any;
        }
    }

    return target;
};

export const createItemConfiguration = (
    metadata?: Metadata, 
    trialConfigurationItems?: ConfigurationSourceDataMap['items'], 
    configurationItems?: ConfigurationSourceDataMap['items'], 
    trial?: Trial, 
    isGlobal?: boolean,
    configurationSourceDataId?: number, 
    studyEventOid?: string,
    caseReportFormOid?: string,
    caseReportFormVersionOid?: string,
    itemGroupName?: string,
    itemName?: string,
    baseRoute?: string
) => {
    
    if (!metadata || !metadata.items || !caseReportFormOid || !caseReportFormVersionOid || !itemName) {
        return undefined;
    }

    const itemMetadata = itemGroupName ? 
        metadata.items?.[caseReportFormOid]?.[caseReportFormVersionOid]?.[itemGroupName]?.[itemName] :
        metadata.items?.[caseReportFormOid]?.[caseReportFormVersionOid]?.[AppSettings.metadata.itemGroupUngroupedKey]?.[itemName];

    if (!itemMetadata) {
        return undefined;
    }

    const itemConfiguration = itemGroupName ?
        configurationItems?.[AppSettings.metadata.studyEventNullKey]?.[caseReportFormOid]?.[caseReportFormVersionOid]?.[itemGroupName]?.[itemName] :
        configurationItems?.[AppSettings.metadata.studyEventNullKey]?.[caseReportFormOid]?.[caseReportFormVersionOid]?.[AppSettings.metadata.itemGroupUngroupedKey]?.[itemName];

    const eventItemConfiguration = studyEventOid ? 
        itemGroupName ?
            configurationItems?.[studyEventOid]?.[caseReportFormOid]?.[caseReportFormVersionOid]?.[itemGroupName]?.[itemName] :
            configurationItems?.[studyEventOid]?.[caseReportFormOid]?.[caseReportFormVersionOid]?.[AppSettings.metadata.itemGroupUngroupedKey]?.[itemName] : 
        undefined;

    const trialItemConfiguration = itemGroupName ?
        trialConfigurationItems?.[AppSettings.metadata.studyEventNullKey]?.[caseReportFormOid]?.[caseReportFormVersionOid]?.[itemGroupName]?.[itemName] :
        trialConfigurationItems?.[AppSettings.metadata.studyEventNullKey]?.[caseReportFormOid]?.[caseReportFormVersionOid]?.[AppSettings.metadata.itemGroupUngroupedKey]?.[itemName];

    const trialEventItemConfiguration = studyEventOid ? 
        itemGroupName ?
            trialConfigurationItems?.[studyEventOid]?.[caseReportFormOid]?.[caseReportFormVersionOid]?.[itemGroupName]?.[itemName] :
            trialConfigurationItems?.[studyEventOid]?.[caseReportFormOid]?.[caseReportFormVersionOid]?.[AppSettings.metadata.itemGroupUngroupedKey]?.[itemName] : 
        undefined;

    const dominantConfiguration = eventItemConfiguration?.linked === false ? 
        eventItemConfiguration : 
        trialEventItemConfiguration?.linked === false ?
            trialEventItemConfiguration :
            itemConfiguration?.linked === false ?
                itemConfiguration :
                trialItemConfiguration

    const parentRoute = baseRoute ?
        baseRoute :
        `/${trial?.oid}/configuration/source-data/${isGlobal ? 'trial' : configurationSourceDataId ? configurationSourceDataId : 'new'}`;

    const configurationLink = itemGroupName ?
        studyEventOid ?
            `${parentRoute}/events/${studyEventOid}/forms/${itemMetadata?.caseReportFormOid}/versions/${itemMetadata.caseReportFormVersionOid}/groups/${itemMetadata.itemGroupName}/items/${itemMetadata.itemName}` :
            `${parentRoute}/forms/${itemMetadata?.caseReportFormOid}/versions/${itemMetadata.caseReportFormVersionOid}/groups/${itemMetadata.itemGroupName}/items/${itemMetadata.itemName}` :
        studyEventOid ?
            `${parentRoute}/events/${studyEventOid}/forms/${itemMetadata?.caseReportFormOid}/versions/${itemMetadata.caseReportFormVersionOid}/items/${itemMetadata.itemName}` :
            `${parentRoute}/forms/${itemMetadata?.caseReportFormOid}/versions/${itemMetadata.caseReportFormVersionOid}/items/${itemMetadata.itemName}`;

    return {
        itemMetadata: itemMetadata,
        itemConfiguration: itemConfiguration,
        eventItemConfiguration: eventItemConfiguration,
        trialItemConfiguration: trialItemConfiguration,
        trialEventItemConfiguration: trialEventItemConfiguration,
        configuration: dominantConfiguration,

        linked: studyEventOid ?
            eventItemConfiguration?.linked !== false :
            itemConfiguration?.linked !== false,

        type: eventItemConfiguration?.linked === false ?
            ConfigurationType.Event :
            trialEventItemConfiguration?.linked === false ?
                ConfigurationType.TrialEvent :
                itemConfiguration?.linked === false ?
                    ConfigurationType.Form :
                    ConfigurationType.Trial,

        icon: AppSettings.icons.item,

        studyEventOid: studyEventOid,
        caseReportFormOid: itemMetadata.caseReportFormOid,
        caseReportFormVersionOid: itemMetadata.caseReportFormVersionOid,
        itemGroupName: itemMetadata.itemGroupName,
        itemName: itemMetadata.itemName,

        displayName: dominantConfiguration?.name ?? itemMetadata?.name,

        priority: dominantConfiguration?.priority,

        configurationLink: configurationLink,
        configurationValuesLink: fieldTypesWithValues.includes(itemMetadata?.fieldType ?? SourceDataFieldType.Text) ? `${configurationLink}/values` : undefined
    };
}

const useItemConfiguration = (
    metadata?: Metadata, 
    trialConfiguration?: ConfigurationSourceDataMap, 
    configuration?: ConfigurationSourceDataMap, 
    trial?: Trial,  
    configurationSourceDataId?: number, 
    studyEventOid?: string,
    caseReportFormOid?: string,
    caseReportFormVersionOid?: string,
    itemGroupName?: string,
    itemName?: string,
    baseRoute?: string
) => {
    return  useMemo(() => {
        return createItemConfiguration(
            metadata, 
            trialConfiguration?.items, 
            configuration?.items, 
            trial, 
            configuration?.configuration?.isGlobal,
            configuration?.configuration?.id ?? configurationSourceDataId, 
            studyEventOid,
            caseReportFormOid,
            caseReportFormVersionOid,
            itemGroupName,
            itemName,
            baseRoute
        );
    }, [metadata, trialConfiguration?.items, configuration?.items, studyEventOid, caseReportFormOid, caseReportFormVersionOid, itemGroupName, itemName, trial, configurationSourceDataId, baseRoute])
};

export default useItemConfiguration;